/**
 * Common search controls above sortable table lists
 * @see ./TablePagination
 * @see ./DataTable
 */
import React from "react";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Clear as ClearIcon, Search as SearchIcon } from "@mui/icons-material";

/**
 *
 * @returns {JSX}
 * @component
 */
const SearchControls = ({
  searchText,
  searchType,
  searchOptions = [],
  onSearchChange,
  onTypeChange,
  onSubmit,
  onClear,
  maxLength = 100,
  // entries per page props
  perPage = 50,
  totalCount = 0,
  onPerPageChange = () => {},
  perPageOptions = [10, 25, 50, 100],
}) => {
  // If searchOptions is empty, provide a default option
  const options =
    searchOptions.length > 0
      ? searchOptions
      : [{ value: searchType || "default", label: "Default" }];

  // Find the current option label safely
  const currentOptionLabel =
    options.find((o) => o.value === searchType)?.label ||
    searchType ||
    "Default";

  // Determine whether to show the entries dropdown or just the total
  const showEntriesDropdown = totalCount > Math.min(...perPageOptions);

  // Handle key press for search
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      onSubmit();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid
        container
        spacing={2}
        sx={{ justifyContent: "space-between" }}
      >
        {/* Entries per page selector - first column */}
        <Grid size={{ xs: 12, sm: 2 }}>
          {showEntriesDropdown ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                columnGap: 1,
                rowGap: 0.75,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 90, flexShrink: 0 }}>
                <InputLabel id="search-controls-per-page-label">
                  Per Page
                </InputLabel>
                <Select
                  labelId="search-controls-per-page-label"
                  value={perPage}
                  onChange={(e) => onPerPageChange(Number(e.target.value))}
                  label="Per Page"
                  variant="outlined"
                >
                  {perPageOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="body2" color="text.secondary">
                /{totalCount}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ pt: 1 }}>
              Total: {totalCount}
            </Typography>
          )}
        </Grid>

        {/* Search text field with integrated buttons */}
        <Grid container size={{ xs: 12, sm: 10 }} spacing={2}>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Search by</InputLabel>
              <Select
                value={searchType}
                onChange={(e) => onTypeChange(e.target.value)}
                label="Search by"
                readOnly={options.length === 1}
                sx={{
                  opacity: options.length === 1 ? 0.5 : 1,
                }}
              >
                {options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 9 }}>
            <TextField
              fullWidth
              label={`Search by ${currentOptionLabel}`}
              variant="outlined"
              value={searchText}
              onChange={(e) => onSearchChange(e.target.value)}
              onKeyUp={handleKeyPress}
              slotProps={{
                htmlInput: {
                  input: maxLength,
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: "flex" }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={onSubmit}
                          sx={{
                            mr: 1,
                            minWidth: "unset",
                            height: 30,
                            borderRadius: "4px",
                          }}
                        >
                          <SearchIcon fontSize="small" />
                          Search
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={onClear}
                          sx={{
                            minWidth: "unset",
                            height: 30,
                            borderRadius: "4px",
                          }}
                        >
                          <ClearIcon fontSize="small" />
                          Clear
                        </Button>
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchControls;
